import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaTimesCircle, FaArrowLeft, FaRedo } from "react-icons/fa";
import { trackEvent } from "../utils/metaPixel";
import { trackButtonClick } from "../utils/analytics";

const PaymentCancel = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Get query parameters from URL
    const urlParams = new URLSearchParams(location.search);
    const orderCode = urlParams.get("orderCode");
    const paymentLinkId = urlParams.get("paymentLinkId");
    const orderId = urlParams.get("orderId");

    // Track payment cancellation
    trackEvent("PaymentCancelled", {
      orderCode,
      paymentLinkId,
      orderId,
      source: "payos_redirect",
    });

    console.log("💳 Payment cancelled:", {
      orderCode,
      paymentLinkId,
      orderId,
      fullUrl: window.location.href,
    });

    // Clean up localStorage
    try {
      const latestPaymentData = localStorage.getItem("latestPaymentData");
      if (latestPaymentData) {
        console.log("🧹 Cleaning up cancelled payment data from localStorage");
        localStorage.removeItem("latestPaymentData");
      }
    } catch (e) {
      console.warn("Could not clean up localStorage:", e);
    }
  }, [location]);

  const handleTryAgain = () => {
    trackButtonClick("try_payment_again");
    navigate("/pricing");
  };

  const handleGoHome = () => {
    trackButtonClick("go_home_after_payment_cancel");
    navigate("/");
  };

  const handleContactSupport = () => {
    trackButtonClick("contact_support_after_payment_cancel");
    window.open("mailto:contact@youhomes.vn?subject=Hỗ trợ thanh toán AIPrice");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center px-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
          <div className="text-center">
            {/* Cancel Icon */}
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-6">
              <FaTimesCircle className="text-red-600 text-2xl" />
            </div>

            {/* Title */}
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
              Thanh toán đã bị hủy
            </h1>

            {/* Description */}
            <p className="text-gray-600 text-lg mb-6">
              Bạn đã hủy quá trình thanh toán. Không có khoản phí nào được tính.
            </p>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8 text-left">
              <h3 className="font-semibold text-blue-900 mb-3 flex items-center">
                <span className="mr-2">💡</span>
                Tại sao nên chọn AIPrice?
              </h3>
              <ul className="text-blue-800 text-sm space-y-2">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Tiết kiệm hàng triệu đồng chi phí thẩm định giá</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Kết quả định giá chính xác trong vòng vài phút</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Báo cáo chuyên nghiệp có thể sử dụng ngay</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Hỗ trợ đa dạng loại bất động sản</span>
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <button
                onClick={handleTryAgain}
                className="inline-flex items-center justify-center px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200"
              >
                <FaRedo className="mr-2" />
                Thử lại thanh toán
              </button>

              <button
                onClick={handleGoHome}
                className="inline-flex items-center justify-center px-8 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-colors duration-200"
              >
                <FaArrowLeft className="mr-2" />
                Về trang chủ
              </button>
            </div>

            {/* Demo Option */}
            {/* <div className="border-t border-gray-200 pt-6">
              <p className="text-gray-600 mb-4">
                Hoặc bạn có thể xem demo miễn phí để hiểu rõ hơn về dịch vụ:
              </p>
              <button
                onClick={() => {
                  trackButtonClick("watch_demo_after_payment_cancel");
                  navigate("/?demo=true");
                }}
                className="px-6 py-2 border-2 border-green-600 text-green-600 rounded-lg font-semibold hover:bg-green-600 hover:text-white transition-colors duration-200"
              >
                Xem Demo Miễn Phí
              </button>
            </div> */}

            {/* Help Text */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-gray-500 text-sm">
                Gặp vấn đề trong quá trình thanh toán?{" "}
                <button
                  onClick={handleContactSupport}
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  Liên hệ hỗ trợ
                </button>
              </p>
            </div>
          </div>
        </div>

        {/* Pricing Preview */}
        {/* <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
            Các gói dịch vụ phổ biến
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="text-center p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
              <h4 className="font-semibold text-gray-900">Gói Cá Nhân</h4>
              <p className="text-2xl font-bold text-blue-600 my-2">29.000₫</p>
              <p className="text-gray-600 text-sm">1 lượt định giá</p>
            </div>
            <div className="text-center p-4 border-2 border-blue-500 rounded-lg bg-blue-50">
              <div className="text-xs bg-blue-500 text-white px-2 py-1 rounded-full inline-block mb-2">
                Phổ biến nhất
              </div>
              <h4 className="font-semibold text-gray-900">Gói Cơ Bản</h4>
              <p className="text-2xl font-bold text-blue-600 my-2">239.000₫</p>
              <p className="text-gray-600 text-sm">10 lượt/tháng</p>
            </div>
            <div className="text-center p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
              <h4 className="font-semibold text-gray-900">Gói Chuyên Nghiệp</h4>
              <p className="text-2xl font-bold text-blue-600 my-2">569.000₫</p>
              <p className="text-gray-600 text-sm">30 lượt/tháng</p>
            </div>
          </div>
          <div className="text-center mt-4">
            <button
              onClick={handleTryAgain}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Xem tất cả gói dịch vụ →
            </button>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default PaymentCancel;
